import React, { useState, useEffect } from 'react';
import {
  Upload,
  Download,
  X,
  AlertCircle,
  Check,
  FileSpreadsheet,
  Package,
  Users,
} from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import authUtils from '../utils/authUtils';

// ===== UTIL =====
const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
  result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : Math.max(0, num);
};

const formatNumber = (number) => {
  if (number === null || number === undefined || number === '') return '0';
  const num = parseInt(number, 10);
  if (isNaN(num)) return '0';
  return num.toLocaleString('vi-VN');
};

const convertDateFormat = (dateStr) => {
  if (!dateStr) return '';

  // yyyy-mm-dd
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // dd/mm/yyyy
  if (typeof dateStr === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Excel serial
  if (typeof dateStr === 'number') {
    const excelEpoch = new Date(Date.UTC(1900, 0, 1));
    const date = new Date(excelEpoch.getTime() + (dateStr - 2) * 24 * 60 * 60 * 1000);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch {
    /* ignore */
  }

  return '';
};

const ImportExcelNhapBaoBiVaPhanBo = ({ onClose, onSuccess }) => {
  const [teamList, setTeamList] = useState([]); // danh sách TỔ Bao bì anh
  const [nccList, setNccList] = useState([]); // Danh sách NCC hợp lệ
  const [loList, setLoList] = useState([]);   // Danh sách Số lô

  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // [{ nhapBaoBi, allocations }]
  const [loading, setLoading] = useState(false);
  const [validationResults, setValidationResults] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // 1. Fetch Teams
      const teamRes = await authUtils.apiRequest('TO_PBNS', 'Find', {});
      const teamsAnh = [
        ...new Set(
          teamRes
            .filter((t) => t['NHÓM'] === 'Bao bì anh')
            .map((t) => t['TỔ'])
        ),
      ].sort();
      setTeamList(teamsAnh);

      // 2. Fetch DSKH (NCC)
      const nccRes = await authUtils.apiRequestKHO('DSKH', 'Find', {});
      setNccList(nccRes.filter(item => item['PHAN_LOAI'] === 'NCC'));

      // 3. Fetch QUANLYLO
      const loRes = await authUtils.apiRequest('QUANLYLO', 'Find', {});
      setLoList(loRes);

    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Lỗi khi tải dữ liệu danh mục (Tổ, NCC, Số lô)');
    } finally {
      setLoading(false);
    }
  };

  // ===== DOWNLOAD TEMPLATE =====
  const handleDownloadTemplate = async () => {
    if (teamList.length === 0) {
      toast.error('Chưa có danh sách tổ Bao bì anh. Hãy kiểm tra bảng TO_PBNS.');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Nhap Bao Bi & BBA');

      // Headers theo đúng thứ tự trên app
      const headers = [
        'NGÀY THÁNG',
        'SỐ XE',
        'NCC',
        'SỐ LÔ',
        'NHẬP BB (KG)',
        'TRỪ BB (KG)',
        'TRẢ BB (KG)',
        'THỰC NHẬN BBE (KG)',
        'SỐ DÂY',
        'GHI CHÚ',
      ];

      // mỗi tổ 2 cột: Số dây + KG
      teamList.forEach((team) => {
        headers.push(`${team} - Số dây`);
        headers.push(`${team} - KG`);
      });

      const headerRow = ws.addRow(headers);
      headerRow.height = 32;

      headers.forEach((h, idx) => {
        const cell = headerRow.getCell(idx + 1);
        let fillColor = 'E0E0E0';
        let fontColor = '000000';

        if (idx <= 3) {
          // NGÀY THÁNG, SỐ XE, NCC, SỐ LÔ
          fillColor = 'FFD966';
          fontColor = '000000';
        } else if (idx >= 4 && idx <= 6) {
          // các cột BB (NHẬP BB, TRỪ BB, TRẢ BB)
          fillColor = 'B4D7FF';
          fontColor = '003366';
        } else if (idx === 7) {
          // THỰC NHẬN BBE
          fillColor = 'FFB6C1';
          fontColor = '8B0000';
        } else if (idx === 8) {
          // SỐ DÂY
          fillColor = '90EE90';
          fontColor = '006400';
        } else if (idx === 9) {
          // GHI CHÚ
          fillColor = 'E0E0E0';
          fontColor = '000000';
        } else {
          // cột tổ
          fillColor = '4472C4';
          fontColor = 'FFFFFF';
        }

        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fillColor },
        };
        cell.font = {
          bold: true,
          color: { argb: fontColor },
          size: 11,
          name: 'Arial',
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
          wrapText: true,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
        };
      });

      ws.columns = [
        { width: 12 }, // NGÀY THÁNG
        { width: 14 }, // SỐ XE
        { width: 26 }, // NCC
        { width: 20 }, // SỐ LÔ
        { width: 14 }, // NHẬP BB
        { width: 14 }, // TRỪ BB
        { width: 14 }, // TRẢ BB
        { width: 18 }, // THỰC NHẬN BBE
        { width: 10 }, // SỐ DÂY
        { width: 25 }, // GHI CHÚ
        ...Array(teamList.length * 2).fill({ width: 14 }), // tổ: Số dây + KG
      ];

      // mẫu 1 - Xe chỉ có BBE (không phân bổ BBA)
      const row1 = [
        '08/01/2026',
        '93H-07422',
        'CÔNG TY A',
        'LO-260114-AD-0001',
        17980,
        540,
        0,
        17440, // Toàn bộ là BBE
        0,
        'Xe mẫu 1 - chỉ có BBE',
      ];
      teamList.forEach(() => {
        row1.push(''); // Số dây
        row1.push(0);  // KG
      });
      ws.addRow(row1);

      // mẫu 2 – có phân bổ BBA cho tổ
      const row2 = [
        '08/01/2026',
        '98L2-67890',
        'CÔNG TY B',
        'LO-260114-AD-0002',
        20000,
        600,
        0,
        5000, // BBE = 5000
        8, // SỐ DÂY
        'Xe mẫu 2 - có BBA',
      ];
      // thucNhanBB = 20000 - 600 - 0 = 19400
      // thucNhanBBA = 19400 - 5000 = 14400
      teamList.forEach((t, i) => {
        if (i === 0) {
          row2.push('4 dây');   // Số dây
          row2.push(7200);      // KG
        } else if (i === 1) {
          row2.push('4 dây');
          row2.push(7200);
        } else {
          row2.push('');
          row2.push(0);
        }
      });
      ws.addRow(row2);

      // mẫu 3 – có TRẢ BB
      const row3 = [
        '08/01/2026',
        '51C-12345',
        'CÔNG TY C',
        'LO-260114-AD-0003',
        15000,
        450,
        2000, // TRẢ BB = 2000
        0,
        6,
        'Xe mẫu 3 - có trả BB',
      ];
      // thucNhanBB = 15000 - 450 - 2000 = 12550
      // thucNhanBBA = 12550 - 0 = 12550
      teamList.forEach((t, i) => {
        if (i === 0) {
          row3.push('6 dây');
          row3.push(12550);
        } else {
          row3.push('');
          row3.push(0);
        }
      });
      ws.addRow(row3);

      // Format cột SỐ XE (cột 2) và SỐ LÔ (cột 4) thành text
      ws.getColumn(2).numFmt = '@'; // SỐ XE
      ws.getColumn(4).numFmt = '@'; // SỐ LÔ

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mau_nhap_bao_bi_va_phan_bo_BBA.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Đã tải file mẫu nhập Bao bì');
    } catch (err) {
      console.error('Error export template:', err);
      toast.error('Lỗi khi xuất file mẫu');
    }
  };

  // ===== UPLOAD & PARSE EXCEL =====
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      toast.error('Vui lòng chọn file Excel (.xlsx, .xls)');
      return;
    }

    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary', cellText: false, cellDates: true });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];

        const json = XLSX.utils.sheet_to_json(ws, {
          raw: false,
          defval: ''
        });

        const validated = validateImportData(json);
        setImportPreview(validated.preview);
        setValidationResults(validated.results);
      } catch (err) {
        console.error('Error reading Excel:', err);
        toast.error('Không thể đọc file Excel');
        setImportFile(null);
        setImportPreview(null);
        setValidationResults(null);
      }
    };
    reader.readAsBinaryString(file);
  };

  // ===== VALIDATE & BUILD DATA =====
  const validateImportData = (rows) => {
    const errors = [];
    const warnings = [];
    const validRecords = [];

    rows.forEach((row, index) => {
      const rowIndex = index + 2; // dòng Excel (bỏ header)
      const rowErrors = [];

      // Đọc các trường cơ bản
      const ngayThang = convertDateFormat(row['NGÀY THÁNG']);
      const soXe = String(row['SỐ XE'] || '').trim();
      const ncc = String(row['NCC'] || '').trim();
      const soLo = String(row['SỐ LÔ'] || '').trim();
      const ghiChu = String(row['GHI CHÚ'] || '').trim();

      // Validate bắt buộc
      if (!ngayThang) rowErrors.push('NGÀY THÁNG không hợp lệ');
      if (!soXe) rowErrors.push('SỐ XE không được để trống');
      if (!ncc) rowErrors.push('NCC không được để trống');

      // === VALIDATE NCC & SỐ LÔ ===
      const validNCC = nccList.find(n => n['TEN_KHACHHANG']?.trim().toLowerCase() === ncc.toLowerCase());
      if (!validNCC) {
        rowErrors.push(`NCC "${ncc}" không tồn tại trong hệ thống hoặc không phải là NCC`);
      }

      if (soLo) {
        const validLo = loList.find(l => l['Số lô']?.trim().toLowerCase() === soLo.toLowerCase());
        if (!validLo) {
          rowErrors.push(`Số lô "${soLo}" không tồn tại trong hệ thống`);
        } else {
          // Kiểm tra lô có thuộc đúng NCC này không
          // Note: So sánh case-insensitive tên NCC trong lô và tên NCC đang nhập
          const loNCC = validLo['Nhà cung cấp']?.trim();
          if (loNCC && loNCC.toLowerCase() !== ncc.toLowerCase()) {
            rowErrors.push(`Số lô "${soLo}" thuộc NCC "${loNCC}", không khớp với NCC đang nhập "${ncc}"`);
          }
        }
      }
      // === END VALIDATE ===

      // Đọc các trường số - theo đúng app
      const nhapBB = parseNumber(row['NHẬP BB (KG)']);
      const truBB = parseNumber(row['TRỪ BB (KG)']);
      const traBB = parseNumber(row['TRẢ BB (KG)']);
      const thucNhanBBE = parseNumber(row['THỰC NHẬN BBE (KG)']);
      const soDay = parseNumber(row['SỐ DÂY']);

      // Tự động tính THỰC NHẬN BB = NHẬP - TRỪ - TRẢ
      const thucNhanBB = Math.max(0, nhapBB - truBB - traBB);

      // Validate
      if (nhapBB <= 0) {
        rowErrors.push('NHẬP BB (KG) phải > 0');
      }
      if (truBB + traBB > nhapBB) {
        rowErrors.push('TRỪ BB + TRẢ BB không được lớn hơn NHẬP BB');
      }
      if (thucNhanBBE > thucNhanBB) {
        rowErrors.push(`THỰC NHẬN BBE (${thucNhanBBE}) không được lớn hơn THỰC NHẬN BB (${thucNhanBB})`);
      }

      // Tự động tính THỰC NHẬN BBA = THỰC NHẬN BB - THỰC NHẬN BBE
      const thucNhanBBA = Math.max(0, thucNhanBB - thucNhanBBE);

      // phân bổ BBA theo tổ
      const allocations = [];
      let totalAllocated = 0;

      teamList.forEach((team) => {
        const colSoDay = `${team} - Số dây`;
        const colKg = `${team} - KG`;

        const soDayAlloc = (row[colSoDay] || '').toString().trim();
        const qty = parseNumber(row[colKg]);

        if (qty > 0) {
          allocations.push({
            teamName: team,
            soDay: soDayAlloc,
            khoiLuong: qty,
          });
          totalAllocated += qty;
        } else if (soDayAlloc && qty === 0) {
          rowErrors.push(`Tổ "${team}": đã nhập Số dây nhưng KG = 0`);
        }
      });

      // Validate tổng phân bổ phải = THỰC NHẬN BBA (nếu có phân bổ)
      if (allocations.length > 0 && Math.abs(totalAllocated - thucNhanBBA) > 1) {
        rowErrors.push(
          `Tổng phân bổ BBA (${formatNumber(totalAllocated)}) phải bằng THỰC NHẬN BBA (${formatNumber(thucNhanBBA)})`
        );
      }

      // Validate SỐ DÂY khi có BBA
      if (thucNhanBBA > 0 && soDay <= 0) {
        rowErrors.push('Có THỰC NHẬN BBA nhưng SỐ DÂY = 0');
      }

      if (rowErrors.length > 0) {
        errors.push(`Dòng ${rowIndex}: ${rowErrors.join('; ')}`);
      } else {
        const id = generateUniqueId();
        validRecords.push({
          nhapBaoBi: {
            ID: id,
            'NGÀY THÁNG': ngayThang,
            'SỐ XE': soXe ? `'${soXe}` : '',
            'NCC': validNCC ? validNCC['TEN_KHACHHANG'] : ncc,
            'SỐ LÔ': soLo ? `'${soLo}` : '',
            'NHẬP BB (KG)': nhapBB,
            'TRỪ BB (KG)': truBB,
            'TRẢ BB (KG)': traBB,
            'THỰC NHẬN BB (KG)': thucNhanBB,
            'THỰC NHẬN BBA (KG)': thucNhanBBA,
            'THỰC NHẬN BBE (KG)': thucNhanBBE,
            'SỐ DÂY': soDay,
            'GHI CHÚ': ghiChu,
          },
          allocations,
        });
      }
    });

    const totalNhapBaoBi = validRecords.length;
    const totalAllocations = validRecords.reduce(
      (sum, r) => sum + r.allocations.length,
      0
    );

    return {
      preview: validRecords,
      results: {
        totalNhapBaoBi,
        totalAllocations,
        errors,
        warnings,
        isValid: errors.length === 0 && totalNhapBaoBi > 0,
      },
    };
  };

  // ===== IMPORT TO DB =====
  const handleImport = async () => {
    if (!validationResults || !validationResults.isValid) {
      toast.error('Dữ liệu không hợp lệ, vui lòng kiểm tra lại.');
      return;
    }

    try {
      setLoading(true);
      const toastId = toast.info('Đang nhập dữ liệu...', { autoClose: false });

      // 1. Lưu NHAPBAOBI
      const nhapBaoBiRows = importPreview.map((r) => r.nhapBaoBi);
      await authUtils.apiRequest('NHAPBAOBI', 'Add', { Rows: nhapBaoBiRows });

      toast.update(toastId, {
        render: `Đã lưu ${nhapBaoBiRows.length} bản ghi NHAPBAOBI...`,
      });

      // 2. Lưu PHANBOBAOBI + NHAPBAOBI_CT
      const phanBoBaoBiRows = [];
      const nhapBaoBiCTRows = [];

      importPreview.forEach((record) => {
        const { nhapBaoBi, allocations } = record;
        const date = nhapBaoBi['NGÀY THÁNG'];

        allocations.forEach((a) => {
          const idPB = generateUniqueId();
          phanBoBaoBiRows.push({
            IDPB: idPB,
            ID_NHAPBAOBI: nhapBaoBi.ID,
            'NGÀY': date,
            'TỔ': a.teamName,
            'TÊN HÀNG': 'Bao bì anh',
            'SỐ DÂY': a.soDay || '',
            'KHỐI LƯỢNG': a.khoiLuong,
          });

          nhapBaoBiCTRows.push({
            ID_CT: generateUniqueId(),
            IDPB_REF: idPB,
            IDCBB_REF: '',
            NGÀY: date,
            'TÊN HÀNG': 'Bao bì anh',
            'NGHIỆP VỤ': 'Nhập',
            'TỔ': a.teamName,
            'SỐ XE': nhapBaoBi['SỐ XE'],
            'NCC': nhapBaoBi['NCC'],
            'SỐ DÂY': a.soDay || '',
            'KHỐI LƯỢNG (KG)': a.khoiLuong,
          });
        });
      });

      if (phanBoBaoBiRows.length > 0) {
        const batchSize = 50;
        for (let i = 0; i < phanBoBaoBiRows.length; i += batchSize) {
          const batch = phanBoBaoBiRows.slice(i, i + batchSize);
          await authUtils.apiRequest('PHANBOBAOBI', 'Add', { Rows: batch });
          toast.update(toastId, {
            render: `Đang lưu PHANBOBAOBI: ${Math.min(
              i + batchSize,
              phanBoBaoBiRows.length
            )}/${phanBoBaoBiRows.length}...`,
          });
        }
      }

      if (nhapBaoBiCTRows.length > 0) {
        const batchSize = 50;
        for (let i = 0; i < nhapBaoBiCTRows.length; i += batchSize) {
          const batch = nhapBaoBiCTRows.slice(i, i + batchSize);
          await authUtils.apiRequest('NHAPBAOBI_CT', 'Add', { Rows: batch });
          toast.update(toastId, {
            render: `Đang lưu NHAPBAOBI_CT: ${Math.min(
              i + batchSize,
              nhapBaoBiCTRows.length
            )}/${nhapBaoBiCTRows.length}...`,
          });
        }
      }

      toast.dismiss(toastId);
      toast.success(
        `Nhập thành công ${nhapBaoBiRows.length} NHAPBAOBI, ${phanBoBaoBiRows.length} PHANBOBAOBI và ${nhapBaoBiCTRows.length} NHAPBAOBI_CT!`
      );

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error importing:', err);
      toast.error('Lỗi khi nhập dữ liệu: ' + (err.message || 'Không xác định'));
    } finally {
      setLoading(false);
    }
  };

  // ===== RENDER =====
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FileSpreadsheet className="h-6 w-6 text-green-600" />
                Nhập Excel BB và phân bổ BBA
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Nhập nhiều xe Bao bì và phân bổ cho từng tổ BBA trong một file Excel.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Hướng dẫn */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Hướng dẫn các cột trong file Excel:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>NGÀY THÁNG, SỐ XE, NCC, SỐ LÔ</strong> - Thông tin cơ bản (bắt buộc)</li>
                  <li><strong>NHẬP BB (KG)</strong> - Khối lượng bao bì nhập vào (bắt buộc)</li>
                  <li><strong>TRỪ BB (KG)</strong> - Khối lượng trừ đi (thường = 3% NHẬP BB)</li>
                  <li><strong>TRẢ BB (KG)</strong> - Khối lượng trả lại (nếu có)</li>
                  <li><strong>THỰC NHẬN BBE (KG)</strong> - Khối lượng Bao bì Em (người dùng nhập)</li>
                  <li><strong>SỐ DÂY</strong> - Tổng số dây (bắt buộc nếu có BBA)</li>
                  <li><strong>Các cột TỔ</strong> - Mỗi tổ có 2 cột: <code>Tổ - Số dây</code> và <code>Tổ - KG</code></li>
                </ul>
                <div className="mt-3 p-2 bg-yellow-100 rounded border border-yellow-300">
                  <p className="font-medium">Công thức tự động tính:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>THỰC NHẬN BB = NHẬP BB - TRỪ BB - TRẢ BB</li>
                    <li>THỰC NHẬN BBA = THỰC NHẬN BB - THỰC NHẬN BBE</li>
                    <li>Tổng KG phân bổ các tổ phải = THỰC NHẬN BBA</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Download template */}
          <div className="flex justify-center">
            <button
              onClick={handleDownloadTemplate}
              disabled={loading || teamList.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4" />
              Tải file mẫu ({teamList.length} tổ BBA)
            </button>
          </div>

          {/* Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload-bba"
              disabled={loading}
            />
            <label
              htmlFor="file-upload-bba"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="w-12 h-12 text-gray-400 mb-3" />
              <span className="text-sm text-gray-600">Nhấp để chọn file Excel</span>
              <span className="text-xs text-gray-500 mt-1">
                Hỗ trợ định dạng: .xlsx, .xls
              </span>
            </label>
          </div>

          {/* File info */}
          {importFile && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {importFile.name}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setImportFile(null);
                    setImportPreview(null);
                    setValidationResults(null);
                  }}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Validation results */}
          {validationResults && (
            <div className="space-y-4">
              {validationResults.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium mb-2">
                        Lỗi ({validationResults.errors.length}):
                      </p>
                      <ul className="list-disc list-inside space-y-1 max-h-60 overflow-y-auto">
                        {validationResults.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {validationResults.isValid && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-medium text-green-800 mb-3">Tổng kết</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-600">NHAPBAOBI</span>
                      </div>
                      <div className="text-lg font-bold text-blue-600">
                        {validationResults.totalNhapBaoBi}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm text-gray-600">
                          PHANBOBAOBI
                        </span>
                      </div>
                      <div className="text-lg font-bold text-indigo-600">
                        {validationResults.totalAllocations}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-gray-600">Trạng thái</span>
                      </div>
                      <div className="text-sm font-bold text-green-600">
                        Dữ liệu hợp lệ
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          {importPreview && importPreview.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-700">
                  Xem trước dữ liệu ({importPreview.length} xe)
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto p-4 space-y-4">
                {importPreview.map((record, idx) => (
                  <div
                    key={idx}
                    className="border border-blue-200 rounded-lg overflow-hidden"
                  >
                    <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Package className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-blue-800">
                            {record.nhapBaoBi['SỐ XE']?.replace(/^'/, '')} – {record.nhapBaoBi['NCC']}
                          </span>
                          <span className="text-xs text-gray-600">
                            {new Date(record.nhapBaoBi['NGÀY THÁNG']).toLocaleDateString('vi-VN')}
                          </span>
                        </div>
                        <div className="flex gap-4 text-xs">
                          <span className="text-blue-600">
                            Nhập: {formatNumber(record.nhapBaoBi['NHẬP BB (KG)'])} KG
                          </span>
                          <span className="text-green-600">
                            Thực nhận: {formatNumber(record.nhapBaoBi['THỰC NHẬN BB (KG)'])} KG
                          </span>
                          <span className="text-teal-600">
                            BBA: {formatNumber(record.nhapBaoBi['THỰC NHẬN BBA (KG)'])} KG
                          </span>
                          <span className="text-pink-600">
                            BBE: {formatNumber(record.nhapBaoBi['THỰC NHẬN BBE (KG)'])} KG
                          </span>
                        </div>
                      </div>
                    </div>
                    {record.allocations.length > 0 && (
                      <div className="p-3 bg-white">
                        <div className="text-xs font-medium text-gray-600 mb-2">
                          Phân bổ BBA ({record.allocations.length} tổ):
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {record.allocations.map((a, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs"
                            >
                              {a.teamName}: {a.soDay || '-'} / {formatNumber(a.khoiLuong)} KG
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Đóng
          </button>
          <button
            onClick={handleImport}
            disabled={
              loading ||
              !validationResults ||
              !validationResults.isValid
            }
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <span className="animate-spin">⏳</span>
                Đang nhập...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Nhập dữ liệu
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportExcelNhapBaoBiVaPhanBo;
